import React, { useEffect, useRef } from 'react';
import { usePlayerControls, usePlaybackState } from './playerStore';

interface TouchGesturesProps {
  containerRef: React.RefObject<HTMLElement>;
  onShowControls?: () => void;
}

export const TouchGestures: React.FC<TouchGesturesProps> = ({ 
  containerRef, 
  onShowControls 
}) => {
  const { togglePlayPause, seek } = usePlayerControls();
  const { currentTime, duration } = usePlaybackState();
  
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const lastTapRef = useRef<number>(0);
  const swipeThreshold = 50; // pixels
  const tapThreshold = 300; // ms
  
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now()
      };
    };
    
    const handleTouchEnd = (e: TouchEvent) => {
      if (!touchStartRef.current) return;
      
      // Don't handle touches on interactive elements
      const target = e.target as HTMLElement;
      if (target.closest('button') || 
          target.closest('[role="button"]') || 
          target.closest('.player-controls') ||
          target.tagName === 'BUTTON') {
        touchStartRef.current = null;
        return;
      }
      
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;
      const deltaTime = Date.now() - touchStartRef.current.time;
      
      // Check for tap
      if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10 && deltaTime < tapThreshold) {
        handleTap(touch.clientX, touch.clientY);
      }
      // Check for horizontal swipe
      else if (Math.abs(deltaX) > swipeThreshold && Math.abs(deltaY) < swipeThreshold) {
        handleSwipe(deltaX);
      }
      // Check for vertical swipe (show/hide controls)
      else if (Math.abs(deltaY) > swipeThreshold && Math.abs(deltaX) < swipeThreshold) {
        if (deltaY < 0) {
          // Swipe up - show controls
          onShowControls?.();
        }
      }
      
      touchStartRef.current = null;
    };
    
    const handleTap = (x: number, y: number) => {
      const now = Date.now();
      const timeSinceLastTap = now - lastTapRef.current;
      
      // Double tap detection
      if (timeSinceLastTap < 300) {
        handleDoubleTap(x, y);
        lastTapRef.current = 0;
      } else {
        lastTapRef.current = now;
        
        // Single tap - play/pause
        setTimeout(() => {
          if (lastTapRef.current === now) {
            togglePlayPause();
          }
        }, 300);
      }
    };
    
    const handleDoubleTap = (x: number, y: number) => {
      const rect = container.getBoundingClientRect();
      const relativeX = x - rect.left;
      const centerX = rect.width / 2;
      
      // Double tap left side - rewind 10s
      if (relativeX < centerX) {
        seek(Math.max(0, currentTime - 10000));
        showFeedback('−10s', x, y);
      }
      // Double tap right side - forward 10s
      else {
        seek(Math.min(duration, currentTime + 10000));
        showFeedback('+10s', x, y);
      }
    };
    
    const handleSwipe = (deltaX: number) => {
      // Swipe right - forward 5s
      if (deltaX > 0) {
        seek(Math.min(duration, currentTime + 5000));
      }
      // Swipe left - rewind 5s
      else {
        seek(Math.max(0, currentTime - 5000));
      }
    };
    
    const showFeedback = (text: string, x: number, y: number) => {
      const feedback = document.createElement('div');
      feedback.textContent = text;
      feedback.style.cssText = `
        position: fixed;
        left: ${x}px;
        top: ${y}px;
        transform: translate(-50%, -50%);
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 8px 16px;
        border-radius: 20px;
        font-size: 18px;
        pointer-events: none;
        z-index: 9999;
        animation: fadeOut 1s ease-out forwards;
      `;
      
      // Add animation
      const style = document.createElement('style');
      style.textContent = `
        @keyframes fadeOut {
          0% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(1.2); }
        }
      `;
      document.head.appendChild(style);
      
      document.body.appendChild(feedback);
      
      setTimeout(() => {
        feedback.remove();
        style.remove();
      }, 1000);
    };
    
    // Pinch to zoom support
    let initialDistance = 0;
    let initialScale = 1;
    
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const distance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );
        
        if (initialDistance === 0) {
          initialDistance = distance;
          return;
        }
        
        const scale = distance / initialDistance;
        
        // Emit custom zoom event
        const zoomEvent = new CustomEvent('player:zoom', {
          detail: { scale: initialScale * scale }
        });
        container.dispatchEvent(zoomEvent);
      }
    };
    
    const handleTouchEndMulti = () => {
      initialDistance = 0;
    };
    
    // Add event listeners
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('touchmove', handleTouchMove);
    container.addEventListener('touchend', handleTouchEndMulti);
    
    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEndMulti);
    };
  }, [containerRef, togglePlayPause, seek, currentTime, duration, onShowControls]);
  
  return null; // This component doesn't render anything
};